import { Component, forwardRef, Input, ViewEncapsulation, ChangeDetectionStrategy, inject, input, output } from '@angular/core';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { TranslocoService } from '@jsverse/transloco';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AraEditorMenuComponent } from './ara-editor-menu/ara-editor-menu.component';
import { NgxTiptapModule } from 'ngx-tiptap';

@Component({
	selector: 'ara-editor',
	templateUrl: './ara-editor.component.html',
	styleUrls: ['./ara-editor.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AraEditorComponent),
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AraEditorMenuComponent, NgxTiptapModule, ReactiveFormsModule, FormsModule],
})
export class AraEditorComponent implements ControlValueAccessor {
	translocoService = inject(TranslocoService);

	_model: string = '';
	readonly modelChange = output<string>();
	readonly placeholder = input<string>('');
	readonly disabled = input(false);

	editor: Editor;

	onChange = (value: string) => {};
	onTouched = () => {};

	constructor() {
		this.editor = new Editor({
			extensions: [
				StarterKit,
				Placeholder.configure({
					placeholder: this.placeholder(),
				}),
				Underline,
			],
			editorProps: {
				attributes: {
					class: 'p-2 focus:border-none border-black border-t-2 outline-none',
					spellcheck: 'false',
				},
			},
		});
	}

	@Input() set model(value: string) {
		this._model = value;
		this.modelChange.emit(this._model);
		this.onChange(value);
	}

	sendValue() {
		this.modelChange.emit(this._model);
		this.onChange(this._model);
	}

	writeValue(value: string): void {
		this._model = value || '';
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		this.editor.setEditable(!isDisabled);
	}
}
